import {Box, Container, SelectChangeEvent, Typography} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {useCallback, useContext, useEffect, useState} from "react";
import SearchForm, {useSearchFormProps} from "../components/forms/SearchForm";
import SortOrder, {useSortOrder} from "../components/forms/SortOrder";
import {FormWorkSearchCond, workAttrList} from "../types/workForm";
import {WorkSortOrderValue} from "../../../constants/constants";
import {CONTEXT_ACTION_TYPE, DataStoreContext, DataStoreType,} from "../contexts/WorkContext";
import useNotification from "../../../utils/notificationUtil";
import {handleInputChangeSelect} from "../../../utils/formUtil";
import {findWork, FindWorkResponseData} from "../../../utils/awsAmplifyUtil";
import {excludeClassesWithChildren, extractDomain} from "../utils/workUtil";
import WorkGroupListItem from "../components/lists/WorkGroupListItem";
import useUser from "../hooks/useUser";
import {equalAdminRoleRule, Role} from "../policies/rules";
import useFetchAllQuery, {FetchMessage, Status, STATUSES} from "../../../hooks/useFetchAllQuery";
import {SearchCond} from "../../handselection/components/forms/SearchForm";
import {WorkClassDocument, WorkInstanceDocument, WorkModelDocument} from "../../../types/pj1dbApiWork";
import {PaginationQuery} from "../../../types/pj1dbApi";

export interface WorkGroup {
  class: WorkClassDocument,
  instances: {
    instance: WorkInstanceDocument,
    models: WorkModelDocument[],
  }[]
}

export default function WorkRegisteredList() {
  const {
    notFoundMsg,
    setSearchCond,
    changeSortOrder,
    searchCond,
    // searchWorks,
    selectedInstanceId,
    changeSelectedInstanceId,
    works,
    searchStatus,
    fetchAll,
  } = useSearchWorkHelpers();

  console.log(works);

  return (
    <>
      <SearchForm
        isLoading={searchStatus === STATUSES.FETCHING} searchCond={searchCond} setSearchCond={setSearchCond}
        onSubmit={(e) => {
          e.preventDefault();
          fetchAll().catch(console.error);
        }}
      />

      <Container
        sx={{p: 0, display: "flex", flexDirection: "row", flexWrap: "wrap"}}
        maxWidth={false}
      >
        <Grid container alignItems="end" sx={{mt: 2, flexWrap: "nowrap"}} size={{xs: 12}}>
          <Grid size="grow">
            <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>
              検索結果一覧
            </Typography>
          </Grid>
          <Grid size="auto">
            <Grid container alignItems="center" spacing={2} sx={{flexWrap: "nowrap"}}>
              <Grid>
                <Box component="span">
                  {searchCond.listCount > 0 ? `${searchCond.listCount.toLocaleString()}件` : ""}
                </Box>
              </Grid>
              <Grid>
                <Box component="span">
                  <SortOrder value={searchCond.sortOrder} onChange={changeSortOrder}/>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <FetchMessage
          data={works}
          status={searchStatus}
          idleMessage={
            <Message>
              別サンプル登録する場合：対象物を検索して登録してください。<br/>
              サンプル情報を修正する場合：対象物を検索して選択後に修正してください。<br/>
              別モデルを登録する場合：対象物を検索して選択後に登録してください。
            </Message>
          }
        />

        <Grid container alignItems="stretch" spacing={2} sx={{mt: 2}}>
          {works.map(row => (
            <Grid key={row.class.attr[workAttrList.classId.key]} size={12}>
              <WorkGroupListItem work={row.class} instances={row.instances} selectedInstanceId={selectedInstanceId}
                                 onChange={changeSelectedInstanceId}/>
            </Grid>
          ))}
        </Grid>

        {(works.length === 0) && (
          <Box sx={{my: 3, width: "100%", textAlign: "center",}}>
            <Typography variant="subtitle1" sx={{fontWeight: "bold"}} dangerouslySetInnerHTML={{__html: notFoundMsg}}/>
          </Box>
        )}
      </Container>
    </>
  )
}

function Message({children}: { children: React.ReactNode }) {
  return (
    <Box sx={{my: 3, width: "100%", textAlign: "center",}}>
      <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>
        {children}
      </Typography>
    </Box>
  )
}

function useSearchWorkHelpers() {
  const {user, role} = useUser();
  const {errorMsg} = useNotification();
  const {state, dispatch} = useContext(DataStoreContext);
  const [notFoundMsg, setNotFoundMsg] = useState("");
  const {searchCond, setSearchCond} = useSearchFormProps();
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
  const [searchStatus, setSearchStatus] = useState<Status>(state.workRegisteredListPage.searchStatus);
  const sortWorks = useSortWorks();
  const [works, setWorks] = useState<WorkGroup[]>(sortWorks(
    convertToList({
      classes: state.workRegisteredListPage.workClasses,
      instances: state.workRegisteredListPage.workInstances,
      models: state.workRegisteredListPage.workModels
    }),
    searchCond.sortOrder
  ));

  const changeSortOrder = (event: SelectChangeEvent<WorkSortOrderValue>) => {
    handleInputChangeSelect<FormWorkSearchCond>(event, "sortOrder", searchCond, setSearchCond);
    dispatch({
      type: CONTEXT_ACTION_TYPE.WORK_REGISTERED_LIST, payload: {
        ...state.workRegisteredListPage,
        searchCond: {
          ...searchCond,
          sortOrder: event.target.value as WorkSortOrderValue
        },
      } satisfies DataStoreType['workRegisteredListPage']
    });
  }

  const changeSelectedInstanceId = (instanceId: string | null) => {
    setSelectedInstanceId(instanceId);
    dispatch({
      type: CONTEXT_ACTION_TYPE.WORK_REGISTERED_LIST, payload: {
        ...state.workRegisteredListPage,
        selectedInstanceId: instanceId,
      } satisfies DataStoreType['workRegisteredListPage']
    });
  }

  const result = useWorks({searchCond, role, email: user.attributes?.email ?? ''});

  useEffect(() => {
    if (!(result.status === searchStatus || (result.status === STATUSES.IDLE && searchStatus === STATUSES.SUCCESS))) {
      setSearchStatus(result.status);
    }
    if (result.status === STATUSES.IDLE) {
      // idle で dispatch すると、タブ切り替え等で以前のデータが消えてしまうので、idle は無視する
      return;
    }
    const newWorks: WorkGroup[] = sortWorks(
      convertToList({classes: result.classes, instances: result.instances, models: result.models}),
      searchCond.sortOrder
    );
    setWorks(newWorks);
    setSearchCond({...searchCond, listCount: newWorks.length});

    dispatch({
      type: CONTEXT_ACTION_TYPE.WORK_REGISTERED_LIST, payload: {
        ...state.workRegisteredListPage,
        workClasses: result.classes,
        workInstances: result.instances,
        workModels: result.models,
        searchStatus: result.status,
      } satisfies DataStoreType['workRegisteredListPage']
    });
    if (result.status === STATUSES.FAILURE && searchStatus !== STATUSES.FAILURE) {
      errorMsg("データ取得中にエラーが発生しました。");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.classes, result.instances, result.models, result.status, searchCond.sortOrder]);

  return {
    errorMsg,
    notFoundMsg,
    setNotFoundMsg,
    searchCond,
    setSearchCond,
    changeSortOrder,
    // searchWorks,
    selectedInstanceId,
    changeSelectedInstanceId,
    works,
    searchStatus,
    fetchAll: result.fetchAll,
  }
}

function useWorks({searchCond, role, email}: { searchCond: SearchCond, role: Role, email: string }) {
  const [status, setStatus] = useState<Status>(STATUSES.IDLE);

  const modelResult = useFetchAllQuery<FindWorkResponseData<'model'>>({
    queryFn: (query) => findModels(role, email, query),
    needToFetchMore: (data, limit) => Object.entries(data?.items ?? {}).length === limit,
  });

  const instanceResult = useFetchAllQuery<FindWorkResponseData<'instance'>>({
    queryFn: (query) => {
      const models = modelResult
        .data
        .reduce((acc, item) => ({...acc, ...item.items}), {} as FindWorkResponseData<'model'>['items']);
      const instanceIds = Object.keys(models).map(key => models[key].attr[workAttrList.instanceId.key]).filter((id): id is string => id !== undefined);
      return findInstances(instanceIds, query);
    },
    needToFetchMore: (data, limit) => Object.entries(data?.items ?? {}).length === limit,
  });

  const classResult = useFetchAllQuery<FindWorkResponseData<'class'>>({
    queryFn: (query) => findClasses(searchCond.keyword, query),
    needToFetchMore: (data, limit) => Object.entries(data?.items ?? {}).length === limit,
  });

  const fetchAll = async () => {
    await Promise.all([
      classResult.fetchAll(),
      instanceResult.fetchAll(),
      modelResult.fetchAll(),
    ])
  }

  useEffect(() => {
    let newStatus: Status = STATUSES.IDLE;
    if (modelResult.isFailure || instanceResult.isFailure || classResult.isFailure) {
      newStatus = STATUSES.FAILURE;
    } else if (modelResult.isSuccess && instanceResult.isSuccess && classResult.isSuccess) {
      newStatus = STATUSES.SUCCESS;
    } else if (modelResult.isFetching || instanceResult.isFetching || classResult.isFetching) {
      newStatus = STATUSES.FETCHING;
    }

    if (status !== newStatus) {
      setStatus(newStatus);
    }
  }, [status, setStatus, modelResult.isFailure, modelResult.isSuccess, modelResult.isFetching, instanceResult.isFailure, instanceResult.isSuccess, instanceResult.isFetching, classResult.isFailure, classResult.isSuccess, classResult.isFetching]);

  return {
    status,
    models: modelResult.data,
    instances: instanceResult.data,
    classes: classResult.data,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    errors: [modelResult.error, instanceResult.error, classResult.error]
      .filter((error) => error !== null && error !== undefined),
    fetchAll,
  }
}

function findModels(role: Role, email: string, query: PaginationQuery) {
  return findWork(
    'model',
    {
      version: "work-user",
      queryAttr: equalAdminRoleRule({role}) ? {} : {
        $and: [
          {
            [workAttrList.creator.key]: {
              "$regex": `^.+@${extractDomain(email) ?? ''}$`,
              "$options": "i"
            }
          },
        ],
      },
    },
    query
  );
}

function findInstances(instanceIds: string[], query: PaginationQuery) {
  return findWork(
    'instance',
    {
      version: "work-user",
      queryAttr: {
        // [workAttrList.instanceId.key]: {"$in": instanceIds}
      },
    },
    query
  );
}

function findClasses(keyword: string, query: PaginationQuery) {
  return findWork(
    'class',
    {
      version: "work-user",
      queryAttr: {
        ...(keyword === "" ? {} : {
          $or: [
            {[workAttrList.nameJp.key]: {"$regex": keyword, "$options": "i"}},
            {[workAttrList.nameEn.key]: {"$regex": keyword, "$options": "i"}},
            {[workAttrList.code.key]: {"$regex": keyword, "$options": "i"}},
            {[workAttrList.manufacturer.key]: {"$regex": keyword, "$options": "i"}},
          ]
        }),
      },
    },
    query
  );
}


function convertToList(params: {
  classes: FindWorkResponseData<"class"> []
  instances: FindWorkResponseData<"instance"> [],
  models: FindWorkResponseData<"model"> [],
}): WorkGroup[] {
  const workClasses = excludeClassesWithChildren(params.classes.map(d => Object.values(d.items)).flat());
  const workInstances = params.instances.map(d => Object.values(d.items)).flat();
  const workModels = params.models.map(d => Object.values(d.items)).flat();

  return workClasses.map((workClass) => {
    const instances = workInstances.filter(instance => instance.attr[workAttrList.classId.key] === workClass.attr[workAttrList.classId.key]);
    return {
      class: workClass,
      instances: instances.map(instance => {
        const models = workModels.filter(model => model.attr[workAttrList.instanceId.key] === instance.attr[workAttrList.instanceId.key]);
        return {instance, models};
      })
    };
  }).filter(item => !(item.instances.length === 0 || item.instances.some(instance => instance.models.length === 0)));
}

function useSortWorks() {
  // TODO: ソートをAPI側に移動する
  const {sortWorkList: baseSortWorkList} = useSortOrder();
  return useCallback((list: WorkGroup[], order: WorkSortOrderValue): WorkGroup[] => {
    const sorted = baseSortWorkList(list.map(w => w.class), order);
    return sorted.map((item) => {
      const work = list
        .find(w => w.class.attr[workAttrList.classId.key] === item.attr[workAttrList.classId.key]);
      if (!work) {
        throw new Error("Work not found");
      }
      return work;
    });
  }, [baseSortWorkList]);
}
